import { createSlice } from "@reduxjs/toolkit";

const defaultProductionOptions = {
  prepayment: null,
  on_income: null,
  after_realization: null,
  delivery_days: null,
  min_quantity_for_income: null,
  box_quantity: null,

  // это не настраивается в разделе
  production_days: null,
  pre_shipment: null,
  safety_stock: null,
};

//----------------------------------------
export const warehousesSettingsInitialState = {
  cabinetId: 0,
  options: {
    fboShare: 100,
    useOnlySelectedProducts: false,
    withMarketContext: false,
    forecastPeriod: 30,
    countAsNewProductPeriodDays: 31,
    include: {
      brandIds: [],
      subjectIds: [],
      productIds: [],
    },
    exclude: {
      isSwitched: true,
      abcGroups: ["A"],
      stockDays: 30,
      stockDaysComp: "gte",
      newProducts: false,
      brandIds: [],
      subjectIds: [],
      productIds: [],
    },
    unexpectedDemandReserve: {
      isSwitched: true,
      coef: 1,
      abcGroups: ["A"],
      stockDays: 30,
      stockDaysComp: "gte",
      newProducts: false,
      brandIds: [],
      subjectIds: [],
      productIds: [],
    },
    minimumBalance: {
      days: 1,
      abcGroups: ["A"],
      stockDays: 30,
      stockDaysComp: "gte",
      newProducts: false,
      brandIds: [],
      subjectIds: [],
      productIds: [],
    },
    distributionConditions: {
      isSwitched: true,
      minQuantity: 5,
      excludeWithSharePercent: 5,
    },
    nearestPossibleSupplyDate: new Date(),
    supplyFrequency: 1,
    supplyPreparationDays: 1,
    rcPrepTimes: [],
    whPrepTimes: [],
    withMoyskladStocks: false,
    transitWarehouse: "",
  },

  filters: {
    currentStep: 1,
    isCheckedExcludeAbc: false,
    isCheckedExcludeStockDays: false,
    isCheckedUnexpectedDemandAbc: false,
    isCheckedUnexpectedDemandStockDays: false,
    isCheckedMinimumBalanceAbc: false,
    isCheckedMinimumBalanceStockDays: false,
  },

  prepTimesInitialized: false,
  warehouseLocations: [],
  warehouseRegions: [],

  productionOptionsByBarcode: {},
};

//-----------------------------------------------
const warehousesSettingsSlice = createSlice({
  name: "warehousesSettings",
  initialState: warehousesSettingsInitialState,
  reducers: {
    loadSettingsObject(state, action) {
      const { cabinetId, options, filters } = action.payload;

      state.cabinetId = cabinetId;

      for (const key in options) {
        if (key === "nearestPossibleSupplyDate" && typeof options[key] === "string") {
          state.options[key] = new Date(options[key]);
          continue;
        }

        if (key === "rcPrepTimes") continue;

        if (Array.isArray(options[key])) {
          state.options[key] = [...options[key]];
        } else if (typeof options[key] === "object" && options[key] !== null) {
          state.options[key] = {
            ...state.options[key],
            ...options[key],
          };
        } else {
          state.options[key] = options[key];
        }
      }

      Object.assign(state.filters, filters);
    },
    updateCabinetId(state, action) {
      state.cabinetId = action.payload;
    },
    initializePrepTimes(state, action) {
      state.prepTimesInitialized = true;
    },
    updateFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    updateOption(state, action) {
      const { key, value } = action.payload;
      state.options[key] = value;
    },
    updateIncludeField(state, action) {
      const { key, value } = action.payload;
      state.options.include[key] = value;
    },
    updateExcludeField(state, action) {
      const { key, value } = action.payload;
      state.options.exclude[key] = value;
    },
    updateUnexpectedDemandField(state, action) {
      const { key, value } = action.payload;
      state.options.unexpectedDemandReserve[key] = value;
    },
    updateMinimumBalanceField(state, action) {
      const { key, value } = action.payload;
      state.options.minimumBalance[key] = value;
    },
    updateDistributionConditionsField(state, action) {
      const { key, value } = action.payload;
      state.options.distributionConditions[key] = value;
    },
    updateRcPrepTimes(state, action) {
      state.options.rcPrepTimes = action.payload;
    },
    updateWhPrepTimes(state, action) {
      state.options.whPrepTimes = action.payload;
    },
    updateWarehouseLocations(state, action) {
      state.warehouseLocations = action.payload;
    },
    updateWarehouseRegions(state, action) {
      state.warehouseRegions = action.payload;
    },
    resetSettings() {
      return warehousesSettingsInitialState;
    },
    resetIncludeSettings(state) {
      state.options.include = {
        ...warehousesSettingsInitialState.options.include,
        brandIds: [...warehousesSettingsInitialState.options.include.brandIds],
        subjectIds: [...warehousesSettingsInitialState.options.include.subjectIds],
        productIds: [...warehousesSettingsInitialState.options.include.productIds],
      };
    },
    resetExcludeSettings(state) {
      state.options.exclude = {
        ...warehousesSettingsInitialState.options.exclude,
        abcGroups: [...warehousesSettingsInitialState.options.exclude.abcGroups],
        brandIds: [...warehousesSettingsInitialState.options.exclude.brandIds],
        subjectIds: [...warehousesSettingsInitialState.options.exclude.subjectIds],
        productIds: [...warehousesSettingsInitialState.options.exclude.productIds],
      };
    },
    resetUnexpectedDemandSettings(state) {
      state.options.unexpectedDemandReserve = {
        ...warehousesSettingsInitialState.options.unexpectedDemandReserve,
        abcGroups: [...warehousesSettingsInitialState.options.unexpectedDemandReserve.abcGroups],
        brandIds: [...warehousesSettingsInitialState.options.unexpectedDemandReserve.brandIds],
        subjectIds: [...warehousesSettingsInitialState.options.unexpectedDemandReserve.subjectIds],
        productIds: [...warehousesSettingsInitialState.options.unexpectedDemandReserve.productIds],
      };
    },
    resetMinimumBalanceSettings(state) {
      state.options.minimumBalance = {
        ...warehousesSettingsInitialState.options.minimumBalance,
        abcGroups: [...warehousesSettingsInitialState.options.minimumBalance.abcGroups],
        brandIds: [...warehousesSettingsInitialState.options.minimumBalance.brandIds],
        subjectIds: [...warehousesSettingsInitialState.options.minimumBalance.subjectIds],
        productIds: [...warehousesSettingsInitialState.options.minimumBalance.productIds],
      };
    },
    updateProductionOption: (state, action) => {
      const { mp_item_id, key, value } = action.payload;
      if (!state.productionOptionsByBarcode[mp_item_id]) {
        state.productionOptionsByBarcode[mp_item_id] = { ...defaultProductionOptions };
      }
      state.productionOptionsByBarcode[mp_item_id][key] = value;
    },
    initializeProductionOptions: (state, action) => {
      const items = action.payload;
      items.forEach(({ mp_item_id, ...fields }) => {
        if (!state.productionOptionsByBarcode[mp_item_id]) {
          state.productionOptionsByBarcode[mp_item_id] = { ...defaultProductionOptions };
        }
        // если какие то поля уже пришли заполненными
        Object.assign(state.productionOptionsByBarcode[mp_item_id], fields);
      });
    },
  },
});

export const {
  loadSettingsObject,
  updateCabinetId,
  updateFilter,
  initializePrepTimes,
  updateOption,
  updateIncludeField,
  updateExcludeField,
  updateUnexpectedDemandField,
  updateMinimumBalanceField,
  updateDistributionConditionsField,
  updateRcPrepTimes,
  updateWhPrepTimes,
  updateWarehouseLocations,
  resetSettings,
  resetIncludeSettings,
  resetExcludeSettings,
  resetUnexpectedDemandSettings,
  resetMinimumBalanceSettings,
  updateWarehouseRegions,
  updateProductionOption,
  initializeProductionOptions,
} = warehousesSettingsSlice.actions;

export default warehousesSettingsSlice.reducer;
